export type ErrorSeverity = 'low' | 'medium' | 'high' | 'critical';
export type ErrorStatus = 'new' | 'investigating' | 'resolved' | 'ignored';

export interface ProcessingError {
  id: string;
  userId?: string;
  userPhone?: string;
  messageId?: string;
  receiptId?: string;
  type: ErrorType;
  severity: ErrorSeverity;
  status: ErrorStatus;
  message: string;
  details?: Record<string, unknown>;
  stackTrace?: string;
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string;
  resolvedBy?: string;
  notes?: string;
}

export type ErrorType =
  | 'ocr_failure'
  | 'extraction_failure'
  | 'validation_error'
  | 'duplicate_message'
  | 'rate_limit'
  | 'api_error'
  | 'webhook_error'
  | 'database_error'
  | 'unknown';

export interface ErrorFilters {
  type?: ErrorType;
  severity?: ErrorSeverity;
  status?: ErrorStatus;
  startDate?: string;
  endDate?: string;
  userId?: string;
  page?: number;
  limit?: number;
  sortBy?: 'createdAt' | 'severity' | 'status';
  sortOrder?: 'asc' | 'desc';
}

export interface Pagination {
  total: number;
  totalPages: number;
  page: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export interface ErrorListResponse {
  errors: ProcessingError[];
  pagination: Pagination;
}

export interface ErrorStats {
  total: number;
  byType: Record<ErrorType, number>;
  bySeverity: Record<ErrorSeverity, number>;
  byStatus: Record<ErrorStatus, number>;
  last24Hours: number;
  last7Days: number;
  resolutionRate: number;
  averageResolutionTime: number;
}

export interface AdminStats {
  users: {
    total: number;
    active: number;
    new24h: number;
    new7d: number;
  };
  transactions: {
    total: number;
    today: number;
    thisWeek: number;
    thisMonth: number;
    totalAmount: number;
  };
  receipts: {
    total: number;
    processed: number;
    pending: number;
    failed: number;
    successRate: number;
  };
  errors: ErrorStats;
  systemHealth: {
    apiLatency: number;
    ocrLatency: number;
    databaseConnections: number;
    queueSize: number;
  };
}

export const ERROR_TYPE_LABELS: Record<ErrorType, string> = {
  ocr_failure: 'OCR Failure',
  extraction_failure: 'Extraction Failure',
  validation_error: 'Validation Error',
  duplicate_message: 'Duplicate Message',
  rate_limit: 'Rate Limit',
  api_error: 'API Error',
  webhook_error: 'Webhook Error',
  database_error: 'Database Error',
  unknown: 'Unknown',
};

export const SEVERITY_COLORS: Record<ErrorSeverity, string> = {
  low: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  high: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  critical: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

export const STATUS_COLORS: Record<ErrorStatus, string> = {
  new: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  investigating: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  resolved: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  ignored: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
};
